import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox, makeStyles, Paper, Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import classNames from 'classnames';

import { formatTonsFromKg } from 'utils/weight';
import { colors } from 'theme';
import { formatPercentage } from 'utils/number';
import TableSeparatorRow from 'components/TableSeparatorRow';
import { Expedition, ExpeditionClientPortion } from 'types';
import { computeRepartitionPercentageFromWeight } from 'services/expedition';
import { copyArrayOfObjectWithoutReference } from 'utils/array';

const useStyles = makeStyles({
  smallTablePaper: {
    maxWidth: '45%',
    borderLeft: `6px solid ${colors.greenPastel}`,
  },
  tablePaper: {
    maxWidth: '65%',
    borderLeft: `6px solid ${colors.greenPastel}`,
  },
  headerRow: {
    '& th': {
      borderWidth: '2px',
    },
  },
  dataRow: {
    '& td': {
      borderBottom: 'none',
    },
  },
  clientHeaderRow: {
    '& th': {
      borderWidth: '2px',
      '&:second-child': {
        width: '20%',
      },
    },
  },
  noBorder: {
    borderBottom: 'none',
  },
  negativeWeight: {
    color: colors.red,
  },
});

type ExpeditionDetailsTabProps = {
  expedition: Expedition;
};
const ExpeditionDetailsTab: React.FC<ExpeditionDetailsTabProps> = ({ expedition }) => {
  const [sortedExpeditionClientPortions, setSortedExpeditionClientPortions] = useState<ExpeditionClientPortion[]>([]);
  const classes = useStyles();
  const { t } = useTranslation();

  useEffect(() => {
    const weightInKg: string[] = [];

    const sortedExpeditionClientPortionsForState: ExpeditionClientPortion[] = copyArrayOfObjectWithoutReference(
      expedition.expeditionClientPortions,
    );

    sortedExpeditionClientPortionsForState.sort((portionA, portionB) =>
      portionA.client.shortName.localeCompare(portionB.client.shortName),
    );

    if (expedition.expeditionClientPortions.some(portion => portion?.percentage)) {
      // Percentage saved in DB (new feature)
      setSortedExpeditionClientPortions(expedition.expeditionClientPortions);
    } else {
      sortedExpeditionClientPortionsForState.forEach((expeditionClientPortion, index) => {
        weightInKg[index] = expeditionClientPortion.weight.toString();
      });

      computeRepartitionPercentageFromWeight(expedition.weight, weightInKg).then(data => {
        sortedExpeditionClientPortionsForState.forEach((expeditionClientPortion, index) => {
          sortedExpeditionClientPortionsForState[index].percentage =
            (data?.repartitionEnPourcentage?.['hydra:member']?.[index] ?? 0) / 100;
        });
        setSortedExpeditionClientPortions(sortedExpeditionClientPortionsForState);
      });
    }
  }, [expedition]);

  return (
    <>
      <Box marginBottom="36px">
        <Paper square className={classes.smallTablePaper}>
          <Box display="flex" marginTop="2em" paddingLeft="21px" paddingRight="21px">
            <Table>
              <TableHead>
                <TableRow className={classes.headerRow}>
                  <TableCell align="right">{t('expeditionCreateForm.conditioningQuantity')}</TableCell>
                  <TableCell align="right">{t('expeditionCreateForm.weight')}</TableCell>
                  <TableCell align="right">{t('expeditionCreateForm.truckIdentifier')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow className={classes.dataRow}>
                  <TableCell align="right">{expedition.conditioningQuantity ?? '-'}</TableCell>
                  <TableCell align="right">{formatTonsFromKg(expedition.weight)}</TableCell>
                  <TableCell align="right">{expedition.truckIdentifier ?? '-'}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </Paper>
      </Box>
      <Box marginBottom="36px">
        <Paper square className={classes.tablePaper}>
          <Box display="flex" marginTop="4em" paddingLeft="21px" paddingRight="21px" paddingBottom="2em">
            <Table size="small">
              <TableHead>
                <TableRow className={classes.clientHeaderRow}>
                  <TableCell align="right">{t('expeditionCreateForm.create.client')}</TableCell>
                  <TableCell align="right">{t('expeditionCreateForm.create.availableStockAtCreation')}</TableCell>
                  <TableCell align="right">{t('expeditionCreateForm.create.distributionInT')}</TableCell>
                  <TableCell align="right">{t('expeditionCreateForm.create.distributionInPercent')}</TableCell>
                  <TableCell align="right"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedExpeditionClientPortions
                  .sort((clientA, clientB) => clientA.client.shortName.localeCompare(clientB.client.shortName))
                  .map((portion, index) => {
                    const isLastRow = index === sortedExpeditionClientPortions.length - 1;
                    const weightIsNegative = !!portion.availableStockWeight && portion.availableStockWeight < 0;
                    return (
                      <TableRow key={portion['@id']}>
                        <TableCell
                          align="right"
                          className={classNames({
                            [classes.noBorder]: isLastRow,
                            [classes.negativeWeight]: weightIsNegative,
                          })}
                        >
                          {portion.client.shortName}
                        </TableCell>
                        <TableCell
                          align="right"
                          width="20%"
                          className={classNames({
                            [classes.noBorder]: isLastRow,
                            [classes.negativeWeight]: weightIsNegative,
                          })}
                        >
                          {formatTonsFromKg(portion.availableStockWeight)}
                        </TableCell>
                        <TableCell
                          align="right"
                          className={classNames({
                            [classes.noBorder]: isLastRow,
                          })}
                        >
                          {formatTonsFromKg(portion.weight)}
                        </TableCell>
                        <TableCell
                          align="right"
                          className={classNames({
                            [classes.noBorder]: isLastRow,
                          })}
                        >
                          {formatPercentage(portion.percentage ?? 0, 4)}
                        </TableCell>
                        <TableCell
                          className={classNames({
                            [classes.noBorder]: isLastRow,
                          })}
                        >
                          <Checkbox disabled checked={portion.weight > 0} color="primary" />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                <TableSeparatorRow greyColorScheme>{t('expeditions.total')}</TableSeparatorRow>
                <TableRow className={classes.dataRow}>
                  <TableCell align="right"></TableCell>
                  <TableCell align="right">-</TableCell>
                  <TableCell align="right">{formatTonsFromKg(expedition.weight)}</TableCell>
                  <TableCell align="right">{formatPercentage(1, 4)}</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </Paper>
      </Box>
    </>
  );
};
export default ExpeditionDetailsTab;
